import React, { useState, useEffect, useRef, useContext } from "react";
import "./loop.scss";
import { Button } from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {
  FormattedText,
  getShortName,
  hasProperty,
  getTextWithTooltip,
} from "../../../utils/helpers";
import { DashboardContext } from "../../../context/DashboardContext";
import { GeneralContext } from "../../../context/GeneralContext";
import Modal from "react-modal";
import { deepCopy } from "../../leftSide/Models/modelLogic";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CustomButton from "../../commonComponents/CustomButton";
import CloseButton from "../../commonComponents/CloseButton";
import deleteIcon from "../../../res/icons/delete.png";
import { useDrag, useDrop } from "react-dnd";

function Chi2LoopEntry(props) {
  const { term, updateTerm, model, updateAllOnOptionChange } = props;
  const { uploadedFiles } = useContext(DashboardContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const [keepCuts, setKeepCuts] = useState(
    hasProperty(term, "keepCuts") ? term.keepCuts : false
  );
  const [selectableFiles, setSelectableFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(
    hasProperty(term, "selectedLoopFiles") ? term.selectedLoopFiles : []
  );
  const [isFileSelectModalOpen, setIsFileSelectModalOpen] = useState(false);
  const lastClickedFileIndexRef = useRef(null);
  const [lastClickedFileId, setLastClickedFileId] = useState(null);
  const [chi2ModelList, setChi2ModelList] = useState(
    hasProperty(term, "modelList") ? term.modelList : []
  );
  const [updatedModelID, setUpdatedModelID] = useState(null);
  const [updatedModelOption, setUpdatedModelOption] = useState(null);
  // const [autofitOpen, setAutofitOpen] = useState(false);
  const updateFromParent = useRef(false);

  const buttonRef = useRef();

  useEffect(() => {
    try {
      if (hasProperty(term, "modelList")) {
        let modelListCopy = deepCopy(chi2ModelList);

        modelListCopy = modelListCopy.map((entry) => {
          const updatedVersion = term.modelList.find(
            (modelEntry) => modelEntry.FE_ID === entry.FE_ID
          );

          if (updatedVersion) {
            return { ...entry, option: updatedVersion.option };
          } else {
            // If model not found in term.modelList, keep original entry
            return entry;
          }
        });

        setChi2ModelList(modelListCopy);
        updateFromParent.current = true;
      }
    } catch (error) {
      recordedErrorLog(
        "Error handling local model list update from term: ",
        error
      );
    }
  }, [term]);

  useEffect(() => {
    try {
      const list = [];
      addModelToList(model, list, 0);
      setChi2ModelList(list);
    } catch (error) {
      recordedErrorLog(
        "Error updating local chi2 list after model changes: ",
        error
      );
    }
  }, [model]);

  useEffect(() => {
    try {
      setSelectableFiles(
        uploadedFiles.map((file) => {
          return { ID: file.ID, name: file.name };
        })
      );
    } catch (error) {
      recordedErrorLog("Error handling selectable files update: ", error);
    }
  }, [uploadedFiles]);

  useEffect(() => {
    try {
      if (!updateFromParent.current) {
        if (updatedModelID === null) {
          updateTerm({ ...term, modelList: chi2ModelList });
        } else {
          updateAllOnOptionChange(updatedModelID, updatedModelOption);
          setUpdatedModelID(null);
          setUpdatedModelOption(null);
        }
      } else {
        updateFromParent.current = false;
      }
    } catch (error) {
      recordedErrorLog("Error updating terms from local changes: ", error);
    }
  }, [chi2ModelList]);

  useEffect(() => {
    updateTerm({ ...term, selectedLoopFiles: selectedFiles });
  }, [selectedFiles]);

  useEffect(() => {
    updateTerm({ ...term, keepCuts: keepCuts });
  }, [keepCuts]);

  const addModelToList = (modelToAdd, list, depth) => {
    try {
      if (
        !chi2ModelList.some(
          (modelEntry) => modelEntry.FE_ID === modelToAdd.FE_ID
        )
      ) {
        list.push({
          name: modelToAdd.displayName,
          FE_ID: modelToAdd.FE_ID,
          depth: depth,
          option: "keep",
        });
      } else {
        const currentEntry = chi2ModelList.find(
          (modelEntry) => modelEntry.FE_ID === modelToAdd.FE_ID
        );

        list.push({
          ...currentEntry,
          name: modelToAdd.displayName,
          depth: depth,
        });
      }

      if (modelToAdd.subModels.length > 0) {
        for (let i = 0; i < modelToAdd.subModels.length; i++) {
          const subModel = modelToAdd.subModels[i];

          addModelToList(subModel, list, depth + 1);
        }
      }
    } catch (error) {
      recordedErrorLog("Error adding model to list: ", error);
    }
  };

  const handleSelectableFileModalClose = () => {
    setIsFileSelectModalOpen(false);
    // Reset last clicked index and file ID when modal closes
    lastClickedFileIndexRef.current = null;
    setLastClickedFileId(null);
  };

  const handleSelectAllFiles = () => {
    try {
      // Replace current selection with all available files
      const allFiles = selectableFiles.map((file) => ({
        ID: file.ID,
        name: file.name,
      }));
      setSelectedFiles(allFiles);
      // Reset last clicked index and file ID
      lastClickedFileIndexRef.current = null;
      setLastClickedFileId(null);
    } catch (error) {
      recordedErrorLog("Select all files handler has failed: ", error);
    }
  };

  const handleAvailableFileClick = (file, event) => {
    try {
      // Prevent text selection when shift-clicking
      if (event && event.shiftKey) {
        event.preventDefault();
      }
      
      const { ID, name } = file;
      const currentFileIndex = selectableFiles.findIndex((f) => f.ID === ID);
      
      // Check if shift key is pressed and we have a previous click
      if (event && event.shiftKey && lastClickedFileIndexRef.current !== null) {
        // Get the range of files to select, excluding the last clicked file
        const startIndex = Math.min(lastClickedFileIndexRef.current, currentFileIndex);
        const endIndex = Math.max(lastClickedFileIndexRef.current, currentFileIndex);
        
        // Get all files in the range, but exclude the last clicked file
        const filesToAdd = selectableFiles.slice(startIndex, endIndex + 1)
          .filter((f, idx) => {
            // Exclude the file at the last clicked index
            const actualIndex = startIndex + idx;
            return actualIndex !== lastClickedFileIndexRef.current;
          });
        
        // Add all files in range (allowing duplicates)
        setSelectedFiles((prev) => {
          const newFiles = filesToAdd.map((f) => ({ ID: f.ID, name: f.name }));
          return [...prev, ...newFiles];
        });
      } else {
        // Normal click: always add the file to the end (allowing duplicates)
        setSelectedFiles((prev) => [...prev, { ID, name }]);
      }
      
      // Update the last clicked index and file ID
      lastClickedFileIndexRef.current = currentFileIndex;
      setLastClickedFileId(ID);
    } catch (error) {
      recordedErrorLog("Available file click handler has failed: ", error);
    }
  };

  const handleDeleteFileAtIndex = (index) => {
    try {
      // Remove the file at the specific index and force re-render
      let newFiles = deepCopy(selectedFiles);
      newFiles.splice(index, 1);
      setSelectedFiles(newFiles);
    } catch (error) {
      recordedErrorLog("Delete file at index handler has failed: ", error);
    }
  };

  const handleReorderFiles = (dragIndex, hoverIndex) => {
    try {
      const newFiles = deepCopy(selectedFiles);
      const draggedFile = newFiles[dragIndex];
      newFiles.splice(dragIndex, 1);
      newFiles.splice(hoverIndex, 0, draggedFile);
      setSelectedFiles(newFiles);
    } catch (error) {
      recordedErrorLog("Reorder files handler has failed: ", error);
    }
  };

  // Draggable file item component
  const DraggableFileItem = ({ file, index, shortName }) => {
    const deleteIconRef = useRef(false);

    const [{ isDragging }, drag] = useDrag({
      type: "file",
      item: { index, file },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: () => {
        // Don't start drag if delete icon was clicked
        return !deleteIconRef.current;
      },
    });

    const [{ isOver }, drop] = useDrop({
      accept: "file",
      hover: (draggedItem) => {
        // Don't do anything if hovering over the same item
        if (draggedItem.index === index) {
          return;
        }
        // Update the dragged item's index for visual feedback
        draggedItem.hoverIndex = index;
      },
      drop: (draggedItem) => {
        // Only reorder when the drag ends
        if (draggedItem.index !== index) {
          handleReorderFiles(draggedItem.index, index);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <li
        key={`${file.ID}-${index}`}
        className={`fileItem ${isDragging ? "dragging" : ""} ${
          isOver ? "dropTarget" : ""
        }`}
        style={{ display: isDragging ? "none" : "flex" }}
      >
        <div ref={(node) => drag(drop(node))} className="dragArea">
          <span className="fileIndex">{index + 1}</span>
          <span className="fileName">
            {file.name.length > 40 ? shortName : file.name}
          </span>
        </div>
        {file.name.length > 40 && (
          <HtmlTooltip
            title={
              <React.Fragment>
                <div className="customTooltip">{file.name}</div>
              </React.Fragment>
            }
          >
            <HelpOutlineIcon className="tooltipIcon" />
          </HtmlTooltip>
        )}
        <img
          src={deleteIcon}
          alt="Remove file from selection"
          className="deleteIcon"
          onMouseDown={(e) => {
            e.stopPropagation();
            deleteIconRef.current = true;
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteFileAtIndex(index);
            // Reset the ref after a short delay
            setTimeout(() => {
              deleteIconRef.current = false;
            }, 100);
          }}
          title="Remove file from selection"
        />
      </li>
    );
  };

  const handleModelOptionChange = (option, index) => {
    try {
      const listCopy = deepCopy(chi2ModelList);
      listCopy[index] = { ...listCopy[index], option: option };
      setChi2ModelList(listCopy);
      setUpdatedModelID(listCopy[index].FE_ID);
      setUpdatedModelOption(option);

      // if(option === "autofit"){

      // }
      // else{

      // }
    } catch (error) {
      recordedErrorLog("Model option change handler error: ", error);
    }
  };

  return (
    <div className="chi2Term">
      <div className="chi2TermTitle">
        <div className="titleText">
          <span className="titleLeft">
            {getTextWithTooltip(term.fileFullName, 40)}
          </span>
          <span className="titleSeparator">x</span>
          <span className="titleRight">
            {getTextWithTooltip(model.displayName, 25)}
            &nbsp;&nbsp; | &nbsp;&nbsp;{" "}
            <FormattedText input={term.quantityName} />
          </span>
        </div>
        <div className="titleSubText">
          <span className="titleLeft">
            {!hasProperty(term, "useFullRange") || term.useFullRange
              ? "Full Range"
              : `Range: min=${term.rangeMin || "N/A"}, max=${
                  term.rangeMax || "N/A"
                }`}
          </span>
          <span className="titleSeparator">|</span>
          <span className="titleRight">Weight: {term.weight}</span>
        </div>
      </div>
      <div className="chi2TermContent">
        <div className="dataFilesSection">
          <div className="dataFiles">
            {/* <Button
              variant="contained"
              className="datafileLoadButton"
              onClick={() => setIsFileSelectModalOpen(true)}
              ref={buttonRef}
              style={{ backgroundColor: "#145d9d" }}
            >
              Load Datafiles
            </Button> */}
            <div className="filesSelectedMessage">
              <div className="messageText">
                {selectedFiles.length === 0 ? (
                  <>Use same file for each iteration.</>
                ) : (
                  <>Data selected for {selectedFiles.length} iterations</>
                )}
              </div>
              <div className="buttonContainer">
                <CustomButton
                  text="Datafile Selection"
                  extraClassnames={["noMargin", "smallLoopChipVersion"]}
                  handleClick={() => setIsFileSelectModalOpen(true)}
                  reference={buttonRef}
                />
              </div>
            </div>
            {selectedFiles.length > 0 && (
              <>
                <div className="selectedFilesList">
                  {selectedFiles.length > 4 ? (
                    <>
                      {selectedFiles.slice(0, 3).map((selected, index) => {
                        return (
                          <div key={`${selected.ID}-${index}`} className="selectedFile">
                            {getTextWithTooltip(selected.name, 50)}
                          </div>
                        );
                      })}
                      <div className="selectedFile moreFiles">...</div>
                    </>
                  ) : (
                    selectedFiles.map((selected, index) => {
                      return (
                        <div key={`${selected.ID}-${index}`} className="selectedFile">
                          {getTextWithTooltip(selected.name, 50)}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="checkboxArea">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={keepCuts}
                          onChange={() => setKeepCuts((old) => !old)}
                        />
                      }
                      label={"Keep same exclusion ranges"}
                    />
                  </FormGroup>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="verticalDivider"></div>
        <div className="modelsSection">
          <div className="models">
            <HtmlTooltip
              title={
                <React.Fragment>
                  <div className="modelOptionsTooltip">
                    Select <b>Reset</b> to return the parameters to the initial
                    values for each new dataset. Select <b>Carry Over</b> to use
                    the parameters from the previous fit as starting values for
                    the next dataset.
                  </div>
                </React.Fragment>
              }
            >
              <div className="modelsHeader">
                Model parameter initialization:
                <HelpOutlineIcon className="helpIcon" />
              </div>
            </HtmlTooltip>
            {chi2ModelList.map((modelEntry, index) => {
              return (
                <div
                  className="modelEntry"
                  key={modelEntry.FE_ID}
                  style={{ marginLeft: `${5 * modelEntry.depth}px` }}
                >
                  <div className="name">
                    {getTextWithTooltip(modelEntry.name, 25)}
                  </div>

                  <div className="buttonsArea">
                    <div
                      className={`button${
                        modelEntry.option === "keep" ? " selected" : ""
                      }`}
                      onClick={() => handleModelOptionChange("keep", index)}
                    >
                      Carry Over
                    </div>
                    <div
                      className={`button${
                        modelEntry.option === "revert" ? " selected" : ""
                      }`}
                      onClick={() => handleModelOptionChange("revert", index)}
                    >
                      Reset
                    </div>
                    {/* <div
                      className={`button${
                        modelEntry.option === "autofit" ? " selected" : ""
                        }`}
                        onClick={() => handleModelOptionChange("autofit", index)}
                        >
                        Autofit
                        </div> */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isFileSelectModalOpen}
        onRequestClose={handleSelectableFileModalClose}
        shouldCloseOnOverlayClick={true}
        contentLabel="File select modal"
        appElement={buttonRef.current}
        style={{
          content: {
            position: "fixed",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            marginBottom: "-50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            height: 500,
            padding: 0,
            border: "1px solid black",
            borderRadius: "10px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: "5000",
          },
        }}
      >
        <div className="fileSelectionModal">
          <div className="modalHeader">
            <h3>Select Data Files for Batch</h3>
            <div className="headerButtons">
              <Button
                onClick={handleSelectAllFiles}
                className="selectAllButton"
                disabled={selectableFiles.length === 0}
              >
                Select All
              </Button>
              <Button
                onClick={() => {
                  setSelectedFiles([]);
                  lastClickedFileIndexRef.current = null;
                  setLastClickedFileId(null);
                }}
                className="clearAllButton"
                disabled={selectedFiles.length === 0}
              >
                Clear All
              </Button>
              <CloseButton onClose={handleSelectableFileModalClose} />
            </div>
          </div>
          <div className="modalContent">
            <div className="availableFilesSection">
              <div className="sectionHeader">
                <h4>Available Files</h4>
              </div>
              <ul className="fileList">
                {selectableFiles.map((file) => {
                  const isSelected = selectedFiles.some(
                    (selected) => selected.ID === file.ID
                  );
                  const isLastClicked = file.ID === lastClickedFileId;
                  
                  return (
                    <li
                      key={file.ID}
                      className={`fileItem ${
                        isLastClicked ? "lastSelected" : ""
                      } ${isSelected ? "isSelected" : ""}`}
                      onClick={(e) => handleAvailableFileClick(file, e)}
                    >
                      {getTextWithTooltip(file.name, 40)}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="modalDivider"></div>
            <div className="selectedFilesSection">
              <div className="sectionHeader">
                <h4>Selected Files ({selectedFiles.length})</h4>
              </div>
              <ul className="fileList">
                {selectedFiles.map((file, index) => {
                  const shortName = getShortName(file.name, 40);
                  return (
                    <DraggableFileItem
                      key={`${file.ID}-${index}`}
                      file={file}
                      index={index}
                      shortName={shortName}
                    />
                  );
                })}
                {selectedFiles.length === 0 && (
                  <li className="noFilesMessage">No files selected</li>
                )}
              </ul>
            </div>
          </div>
          <div className="modalFooter">
            <p>
              The selected data files will be analyzed in sequence, starting
              with the first, even if this is not the original file of the Χ
              <sup>2</sup> term.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Chi2LoopEntry;
